/**
 * Shared data hook for the all-pipelines drawer (SE-5489) — the recruitment-
 * pipeline sibling of `useSeeAllListsData`.
 *
 * Owns the parts of the drawer that are pure data transform:
 *  - fetching the current user's kanban boards (skipped while closed),
 *  - keeping only PIPELINE-source boards (the switcher's scope),
 *  - grouping them into Personal / Team / System per the DECIDED, owner-
 *    approved semantics, and
 *  - sorting each group by `updatedAt` DESC (the drawer row's "last activity")
 *    with an optional case-insensitive client-side name filter.
 *
 * Grouping (mutually exclusive, evaluated in this precedence):
 *  1. System  — a seeded template board (`pipelineTemplate` present and not
 *     CUSTOM). Seeded boards are their own bucket regardless of who created
 *     them, so a template never masquerades as a personal board.
 *  2. Personal — a board the current user created (`createdBy === me`).
 *  3. Team    — an accessible board the user did NOT create (shared with them;
 *     `listMyKanbanBoards` only returns boards the user can access, so a
 *     non-creator non-template board is a shared/team board).
 *
 * Per-row card counts are intentionally NOT fetched here: there is no board-
 * level count field, so the drawer fans a bounded per-board count query out
 * from each rendered row (see `PipelineDrawerRow`) rather than eagerly across
 * every board.
 * @module features/player-kanban/hooks/useSeeAllPipelinesData
 */

import { useMemo } from "react";

import {
  type PipelineSwitcherEdge,
  usePipelineSwitcherBoards,
} from "./usePipelineSwitcherBoards";

import { useCurrentUser } from "@/features/auth";
import { KanbanPipelineTemplate, KanbanSourceType } from "@/generated/graphql";

/** Which grouping bucket a pipeline board falls into. */
export type PipelineGroup = "personal" | "team" | "system";

/** A single row rendered inside a drawer section. */
export interface PipelineDrawerRow {
  /** Board id — selection target + testID composition. */
  readonly id: string;
  /** Board display name. */
  readonly name: string;
  /** Which section this row belongs to. */
  readonly group: PipelineGroup;
  /** Human "type" label derived from the pipeline template. */
  readonly typeLabel: string;
  /** Owner/assignee label — the board creator's email (or a fallback). */
  readonly ownerLabel: string;
  /** Last-activity timestamp (board `updatedAt`), ISO string. */
  readonly lastActivityAt: string;
}

/**
 * Board edge shape from the switcher query, narrowed locally.
 */
type SwitcherBoardNode = PipelineSwitcherEdge["node"];

/**
 * Human label for a pipeline template ("type" column). Falls back to "Custom"
 * for CUSTOM or an absent template.
 * @param template - The board's pipeline template, if any.
 * @returns A reader-friendly type label.
 */
export const pipelineTemplateLabel = (
  template: KanbanPipelineTemplate | null | undefined
): string => {
  switch (template) {
    case KanbanPipelineTemplate.NewAcquisitions:
      return "New acquisitions";
    case KanbanPipelineTemplate.AcademyPromotions:
      return "Academy promotions";
    case KanbanPipelineTemplate.OutgoingTransfers:
      return "Outgoing transfers";
    default:
      return "Custom";
  }
};

/**
 * Classify a board into its group bucket per the DECIDED precedence.
 * @param board - The board node.
 * @param currentUserId - The signed-in user's id (undefined when unresolved).
 * @returns The board's group.
 */
export const classifyPipelineGroup = (
  board: Pick<SwitcherBoardNode, "createdBy" | "pipelineTemplate">,
  currentUserId: string | undefined
): PipelineGroup => {
  const isSystem =
    board.pipelineTemplate != null &&
    board.pipelineTemplate !== KanbanPipelineTemplate.Custom;
  if (isSystem) return "system";
  if (currentUserId && board.createdBy === currentUserId) return "personal";
  return "team";
};

/**
 * Sort by `updatedAt` DESC. Unparseable timestamps sink to the bottom.
 * @param a - First row.
 * @param b - Second row.
 * @returns Negative when `a` is more recent, positive when `b` is.
 */
const sortByUpdatedAtDesc = (
  a: PipelineDrawerRow,
  b: PipelineDrawerRow
): number => {
  const aMs = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
  const bMs = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
  const safeA = Number.isFinite(aMs) ? aMs : 0;
  const safeB = Number.isFinite(bMs) ? bMs : 0;
  return safeB - safeA;
};

/**
 * Sort by name, case-insensitively and A→Z.
 * @param a - First row.
 * @param b - Second row.
 * @returns Standard comparator ordering.
 */
const sortByNameAsc = (a: PipelineDrawerRow, b: PipelineDrawerRow): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/**
 * The drawer's row ordering.
 *
 * Both orderings are computed from fields every row already carries, so
 * neither is invented: `recent` reads `updatedAt` (the same value the row
 * prints as "last activity") and `name` reads the name the row prints. There
 * is deliberately no third option — anything richer (most cards, most active
 * this week) would need data the drawer does not load.
 */
export type PipelineDrawerSort = "recent" | "name";

/** Comparator per ordering. */
const SORT_COMPARATOR: Record<
  PipelineDrawerSort,
  (a: PipelineDrawerRow, b: PipelineDrawerRow) => number
> = {
  recent: sortByUpdatedAtDesc,
  name: sortByNameAsc,
};

/** Arguments for {@link useSeeAllPipelinesData}. */
export interface UseSeeAllPipelinesDataArgs {
  /** Whether the drawer is open. Gates the query's `skip` so a closed drawer does not fetch. */
  readonly isOpen: boolean;
  /** Optional case-insensitive substring filter applied to all groups by board name. */
  readonly searchQuery?: string;
  /** Row ordering within each group. Defaults to `recent`. */
  readonly sortOrder?: PipelineDrawerSort;
}

/** Return shape of {@link useSeeAllPipelinesData}. */
export interface UseSeeAllPipelinesDataResult {
  /** Apollo loading state for the switcher board query. */
  readonly loading: boolean;
  /**
   * Whether the switcher board query failed. Consumers render a dedicated
   * error state from this so a network/GraphQL failure is not mistaken for an
   * empty "No pipelines found" result.
   */
  readonly error: boolean;
  /** Retry the board fetch after an error (drives the error-state retry action). */
  readonly retry: () => void;
  /**
   * SE-6775: true when the switcher's board walk stopped while the connection
   * still reported more pages — so pipelines exist that these three groups do
   * NOT contain, and the per-section counts are lower bounds.
   *
   * Passed straight through from `usePipelineSwitcherBoards`, where SE-6597
   * added it under a name deliberately matching SE-6575's
   * `useAllMyKanbanBoards`. This hook previously dropped it, which is what let
   * the "see ALL pipelines" drawer — the one surface whose entire purpose is
   * completeness — present a capped board set as the whole set.
   */
  readonly hasUnreachableBoards: boolean;
  /** Personal rows (created by the current user), sorted + filtered. */
  readonly personal: readonly PipelineDrawerRow[];
  /** Team rows (shared, not created by the user), sorted + filtered. */
  readonly team: readonly PipelineDrawerRow[];
  /** System rows (seeded template boards), sorted + filtered. */
  readonly system: readonly PipelineDrawerRow[];
}

/**
 * Query -> grouped/sorted/filtered rows transform for the all-pipelines drawer.
 * @param args - {@link UseSeeAllPipelinesDataArgs}
 * @param args.isOpen - Whether the drawer is open (drives query skip).
 * @param args.searchQuery - Optional case-insensitive name filter (default "").
 * @param args.sortOrder - Row ordering within each group (default "recent").
 * @returns Loading flag + the three grouped, sorted, filtered row lists.
 */
export const useSeeAllPipelinesData = ({
  isOpen,
  searchQuery = "",
  sortOrder = "recent",
}: UseSeeAllPipelinesDataArgs): UseSeeAllPipelinesDataResult => {
  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id;

  const {
    edges: boardEdges,
    loading,
    error,
    retry,
    hasUnreachableBoards,
  } = usePipelineSwitcherBoards({
    skip: !isOpen,
  });

  const { personal, team, system } = useMemo((): {
    readonly personal: readonly PipelineDrawerRow[];
    readonly team: readonly PipelineDrawerRow[];
    readonly system: readonly PipelineDrawerRow[];
  } => {
    const edges = boardEdges;
    const needle = searchQuery.trim().toLowerCase();

    const rows: PipelineDrawerRow[] = edges
      .map(edge => edge.node)
      .filter(node => node.sourceType === KanbanSourceType.Pipeline)
      .filter(node => needle === "" || node.name.toLowerCase().includes(needle))
      .map(node => ({
        id: node.id,
        name: node.name,
        group: classifyPipelineGroup(node, currentUserId),
        typeLabel: pipelineTemplateLabel(node.pipelineTemplate),
        ownerLabel: node.creator?.email ?? "Unknown owner",
        lastActivityAt: node.updatedAt,
      }));

    const inGroup = (group: PipelineGroup): PipelineDrawerRow[] =>
      // SE-5132: Hermes lacks Array.prototype.toSorted — clone before sorting.
      rows
        .filter(row => row.group === group)
        .slice()
        .sort(SORT_COMPARATOR[sortOrder]);

    return {
      personal: inGroup("personal"),
      team: inGroup("team"),
      system: inGroup("system"),
    };
  }, [boardEdges, searchQuery, sortOrder, currentUserId]);

  return {
    loading,
    error,
    retry,
    personal,
    team,
    system,
    hasUnreachableBoards,
  };
};
